import RandomPrizeIcon from 'assets/images/random-prize.png'

import { FC } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
// Async
import { getResult } from 'store/player/playerAsync';
// Selectors
import { selectQuiz } from 'store/quiz/quizSelectors';
import { selectResult, selectLeaderboard, selectPreviewPageMode, selectResultUploadedAt } from 'store/player/playerSelectors';
// Models
import IQuiz from 'models/Quiz';
// Types
import QuizTypes from 'types/QuizTypes';
// Components
import AppBar from 'components/AppBar';
import TextCountdown from 'components/TextCountdown';
import SocialShare from 'components/SocialShare';
import { Button } from 'components/Buttons';
import PoweredBy from 'components/PoweredBy';
// Hooks
import useNavigateTo from 'hooks/useNavigateTo';
// Components
import PublicDevicePlayable from 'components/PublicDevicePlayable';
// Icons
import { FiRefreshCcw } from 'react-icons/fi';
// Utilities
import { buildUserName, getEndOfPlace } from 'utilities/Utilities';
// i18next
import { useTranslation } from 'react-i18next';

const LeaderboardPage:FC = () => {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug:string }>();

  const dispatch = useAppDispatch();
  const navigateTo = useNavigateTo();

  const quiz:IQuiz = useAppSelector(selectQuiz);
  const result:any = useAppSelector(selectResult);
  const resultUploadedAt = useAppSelector(selectResultUploadedAt);
  const leaderboard:any[] = useAppSelector(selectLeaderboard);
  const previewPageMode = useAppSelector(selectPreviewPageMode);

  const handleGetResult = () => {
    dispatch(getResult({ quizName: slug, limit: 96 }))
  }

  if (!result && !previewPageMode) return null;

  const { socialShare } = quiz.resultsPage;

  const getWidth = (score:number) => {
    const firstPlace = leaderboard?.[0]?.score;
    const percentage = (score / firstPlace) * 100;
    return percentage > 100 ? 100 : percentage;
  }

  const quizEnded = dayjs().isAfter(+result?.quiz.endsDate);
  const { numberOfQuestions, numberOfAnsweredQuestions } = result?.quiz || {};

  return (
    <div
      className="grow flex flex-col"
      style={{ background: quiz.backgroundImage ? `url('${quiz.backgroundImage}') top / cover` : '' }}
    >
      <PublicDevicePlayable />
      <div
        className="px-4 pt-4 grow flex flex-col overflow-y-auto"
      >
        <AppBar handleBack={() => navigateTo('post-quiz')} />
        <div className="flex items-center my-4">
          <div className="grow flex justify-center items-center gap-3">
            <h2 className="font-bold text-center text-primary text-4xl">
              {t('pages.leaderboard.title')}
            </h2>
            {!quizEnded || !quiz.endsDate ? (
              <Button onClick={handleGetResult} size="small">
                <FiRefreshCcw />
              </Button>
            ) : null}
          </div>
        </div>
        {quiz.type === QuizTypes.Prediction && (
          quizEnded ? (
            <p className="text-center text-sm text-primary mb-4">
              {numberOfQuestions === numberOfAnsweredQuestions
                ? t('pages.leaderboard.finalResults')
                : t('pages.leaderboard.answeredQuestions', { questions: numberOfQuestions, answeredQuestions: numberOfAnsweredQuestions })
              }
            </p>
          ) : (
            <p className="text-center text-xs text-primary mb-4">
              {t('pages.leaderboard.predictionHint')}
            </p>
          )
        )}
        {[QuizTypes.Personality, QuizTypes.Scoring].includes(quiz.type) && (
          <TextCountdown startsDate={quiz.startsDate} endsDate={quiz.endsDate} />
        )}
        {resultUploadedAt && (!quizEnded || !quiz.endsDate) ? (
          <p className="text-center text-primary text-sm mb-4">
            {t('pages.leaderboard.lastRefresh', { dateTime: dayjs(resultUploadedAt).format('MMM D, YYYY HH:mm') })}
          </p>
        ) : null}
        {result?.quiz?.quizGroup?.slug ? (
          <div className="text-center mb-4">
            <Button
              className="capitalize"
              onClick={() => {
                navigateTo('quiz-group', {}, result.quiz.quizGroup.slug)
              }}
              disabled={!result}
            >{t('pages.leaderboard.leagueDetailsButton')}</Button>
          </div>
        ) : null}
        {socialShare?.enabled && (socialShare.hashtags || socialShare.url || socialShare.image) ? (
          <div className="mb-4">
            {!!socialShare.bonusValue && (
              <h3 className="text-center text-sm font-bold mb-2 px-4 text-primary">
                {t('pages.leaderboard.shareText', { points: socialShare.bonusValue })}
              </h3>
            )}
            <SocialShare />
          </div>
        ) : null}
        {!previewPageMode && result.currentUserResult ? (
          <div className="mb-4">
            <h3 className="text-primary text-xl font-bold mb-2">{t('labels.yourScore')}</h3>
            <div
              className="
                relative overflow-hidden
                px-4 py-1 flex items-center gap-6
                bg-gradient-to-b from-white to-gray-300
                text-lg
                rounded-lg
              "
              style={{ color: quiz.resultsPage.currentPlacingFontColor }}
            >
              <div
                className="absolute top-0 left-0 h-full rounded-lg"
                style={{
                  width: `${getWidth(result.currentUserResult.score)}%`,
                  background: `linear-gradient(to right, ${quiz.resultsPage.currentPlacingGradientStart}, ${quiz.resultsPage.currentPlacingGradientEnd})`
                }}
              />
              <p className="w-8 z-10 font-bold">{`${result.currentUserResult.position}${getEndOfPlace(result.currentUserResult.position)}`}</p>
              <p className="z-10 flex-1 whitespace-nowrap text-ellipsis overflow-hidden">{buildUserName(result.currentUserResult)}</p>
              <p className="z-10">{result.currentUserResult.score}</p>
            </div>
          </div>
        ) : null}
        {result?.spotPrizeWinnerResult ? (
          <div>
            <h3 className="text-primary text-xl font-bold mb-2">{t('labels.randomPrizeWinner')}</h3>
            <ul>
              <li
                className={`
                  relative overflow-hidden flex items-center
                  px-4 py-1 bg-gradient-to-b from-white to-gray-300 text-lg rounded-lg mb-2 gap-6
                `}
                style={{ color: quiz.resultsPage.placingFontColor }}
              >
                <div
                  className="absolute top-0 left-0 h-full rounded-lg"
                  style={{
                    width: `${getWidth(result.spotPrizeWinnerResult.score)}%`,
                    background: `linear-gradient(to right, ${quiz.resultsPage.placingGradientStart}, ${quiz.resultsPage.placingGradientEnd})`
                  }}
                />
                <div className="w-8 z-10">
                  <img src={RandomPrizeIcon} alt="Random-Prize" className="h-5" /> 
                </div>
                <p className="z-10 flex-1 whitespace-nowrap text-ellipsis overflow-hidden">{buildUserName(result.spotPrizeWinnerResult)}</p>
                <p className="z-10">{result.spotPrizeWinnerResult.score}</p>
              </li>
            </ul>
          </div>
        ) : null}
        {leaderboard.length ? (
          <div>
            <h3 className="text-primary text-xl font-bold mb-2">{t('labels.topPlayers')}</h3>
            <ul>
              {leaderboard.map((result:any, index:number) => (
                <li
                  key={`leaderboard-item-${index}`}
                  className={`
                    relative overflow-hidden flex items-center
                    px-4 py-1 bg-gradient-to-b from-white to-gray-300 text-lg rounded-lg mb-2 gap-6
                  `}
                  style={{ color: quiz.resultsPage.placingFontColor }}
                >
                  <div
                    className="absolute top-0 left-0 h-full rounded-lg"
                    style={{
                      width: `${getWidth(result.score)}%`,
                      background: `linear-gradient(to right, ${quiz.resultsPage.placingGradientStart}, ${quiz.resultsPage.placingGradientEnd})`
                    }}
                  />
                  <p className="w-8 z-10 font-bold">{`${result.position}${getEndOfPlace(result.position)}`}</p>
                  <p className="z-10 flex-1 whitespace-nowrap text-ellipsis overflow-hidden">{buildUserName(result)}</p>
                  <p className="z-10">{result.score}</p>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <div className="flex-1" />
        <PoweredBy />
      </div>
    </div>
  )
}

export default LeaderboardPage;
